import { Injectable } from '@angular/core';
import { User } from '@firebase/auth';
import { deleteDoc, doc } from '@firebase/firestore';
import { ApiService } from './api.service';
import { CartService } from './cart.service';
import { FirebaseInitService } from './firebase-init.service';
import { UserAdditionalDataService } from './user-additional-data.service';
import { UserDataService } from './user-data.service';
import { MongoService } from './mongo.service';
import { StateService } from './state.service';
import { CampaignType } from './campaign.types';

type OrderType = CampaignType;

@Injectable({
  providedIn: 'root'
})
export class UserOrdersService {
  private ordersByType: Record<OrderType, Array<any>> = {
    airconditioners: [],
    pumps: [],
  };

  constructor(
    private firebaseService: FirebaseInitService,
    private apiService: ApiService,
    private mongoService: MongoService,
    private stateService: StateService
  ) {}

  /** Fix: poprawne mapowanie state -> typ zamówień */
  private getCurrentOrderType(): OrderType | null {
    const state = this.stateService.getState();
    if (state === 'state-pumps') return 'pumps';
    if (state === 'state-airconditioners') return 'airconditioners';
    return null;
  }

  public get didUserPlaceOrder(): boolean {
    const type = this.getCurrentOrderType();
    if (!type) return true; // zachowuję Twoje "else return true"
    return this.didUserPlacedOrder(type);
  }

  public didUserPlacedOrder(type: OrderType): boolean {
    return (this.ordersByType[type]?.length ?? 0) > 0;
  }

  public async checkIfUserPlacedOrders(currentUser: User): Promise<void> {
    if (!currentUser) return;

    // szybciej i czyściej: równolegle
    await Promise.all([
      this.refreshUserOrders('airconditioners', currentUser),
      this.refreshUserOrders('pumps', currentUser),
    ]);
  }

  // Zamiast checkIfUserPlacedAirconditionersOrder / checkIfUserPlacedPumpsOrder
  public async checkIfUserPlacedOrder(type: OrderType, currentUser: User): Promise<boolean> {
    if (!currentUser) return false;
    await this.refreshUserOrders(type, currentUser);
    return this.didUserPlacedOrder(type);
  }

  // Jedna metoda do składania zamówień
  public async placeUserOrder(
    type: OrderType,
    cartService: CartService,
    currentUser: User,
    userDataService: UserDataService,
    userAdditionalDataService: UserAdditionalDataService
  ): Promise<boolean> {
    if (!currentUser) return false;
    if (!type) return false;
    if (this.didUserPlacedOrder(type)) return false;

    // I4: kampania koszyka musi zgadzać się z typem zamówienia.
    if (cartService.campaignType !== type) return false;

    // Wybór Paczkomatu jest obowiązkowy (walidacja frontu — wiążącą robi backend).
    const parcelLocker = cartService.selectedParcelLocker;
    if (!parcelLocker || !parcelLocker.name) return false;

    // I3 (klient): każdy benefit w koszyku musi należeć do kolekcji tej kampanii.
    // Wczesne, czytelne odrzucenie zanim cokolwiek poleci do API.
    const cartIsValid = await this.validateCartBenefitsBelongToCampaign(type, cartService);
    if (!cartIsValid) return false;

    const orderData: any = this.generateOrderObject(type, cartService, currentUser);
    const emailTemplateData: any = await this.generateConfirmationEmailTemplateData(
      type,
      cartService,
      orderData,
      userDataService
    );
    const saved = await this.addOrderToStorage(type, orderData);
    if (!saved) return false;

    // Potwierdź zapis przez ponowne pobranie (tak jak robiłeś wcześniej)
    await this.refreshUserOrders(type, currentUser);
    if (this.didUserPlacedOrder(type)) {
      this.apiService.sendOrderConfirmationEmail(emailTemplateData);
    }

    return true;
  }

  // I3 (klient): sprawdza, że wszystkie benefitId z koszyka istnieją w kolekcji
  // benefitów właściwej dla danej kampanii. Defensywna pre-walidacja UX —
  // ostateczną, wiążącą weryfikację wykonuje backend.
  private async validateCartBenefitsBelongToCampaign(type: OrderType, cartService: CartService): Promise<boolean> {
    const items = cartService?.cartItems ?? [];
    if (items.length === 0) return false;
    const benefits = type === 'pumps'
      ? await this.mongoService.getAllPumpsBenefitsData()
      : await this.mongoService.getAllAirconditionersBenefitsData();
    const validIds = new Set((benefits ?? []).map((b: any) => String(b._id)));
    return items.every((i: any) => validIds.has(String(i.benefitId)));
  }

  // Zwraca zamówienie zalogowanego użytkownika dla bieżącej kampanii (wg stanu),
  // lub null gdy brak zamówienia / stan neutralny. Użytkownik składa max 1 zamówienie na kampanię.
  public async getCurrentCampaignOrder(currentUser: User): Promise<any | null> {
    const type = this.getCurrentOrderType();
    if (!type || !currentUser) {
      return null;
    }
    const orders = await this.getUserOrders(type, currentUser);
    return (orders && orders.length > 0) ? orders[0] : null;
  }

  // Zwraca zamówienie użytkownika o danym emailu dla bieżącej kampanii (tryb admina).
  // Świadomie dopasowuje po ZNORMALIZOWANYM emailu na pełnej kolekcji zamówień, a NIE
  // przez endpoint *ordersbyemail — ten robi exact-match wrażliwy na wielkość liter,
  // a zamówienie trzyma surowy email z Firebase (inna wielkość niż lowercase w usersData).
  // Dzięki temu detekcja zamówienia jest spójna z listą zarządzania użytkownikami.
  public async getCurrentCampaignOrderByEmail(userEmail: string): Promise<any | null> {
    const normalized = String(userEmail ?? '').toLowerCase().trim();
    if (!this.getCurrentOrderType() || !normalized) {
      return null;
    }
    const orders = await this.getAllExistingOrders();
    return (orders ?? []).find(
      (order: any) => String(order?.userEmail ?? '').toLowerCase().trim() === normalized
    ) ?? null;
  }

  public async getAllExistingOrders(): Promise<Array<any>> {
    try {
      const orderType = this.getCurrentOrderType();
      if (orderType === 'pumps'){
        return await this.mongoService.getPumpsUsersOrders();
      } else return await this.mongoService.getAirconditionersUsersOrders();      
    } catch (e) {
      console.log(e);
      return [];
    }
  }

  // public async clearOrdersData(): Promise<void> {
  //   const allOrders = await this.getAllExistingOrders();
  //   if (allOrders?.length > 0) {
  //     for (let i = 0; i < allOrders.length; i++) {
  //       await deleteDoc(doc(this.firebaseService.database, 'userOrders', allOrders[i]._id));
  //     }
  //   }
  // }

  // ---- prywatne helpery ----

  private async refreshUserOrders(type: OrderType, currentUser: User): Promise<void> {
    this.ordersByType[type] = await this.getUserOrders(type, currentUser);
  }

  private async addOrderToStorage(type: OrderType, orderObject: any): Promise<boolean> {
    try {
      // I3 (serwer): zapis przez dedykowany endpoint, który po stronie backendu
      // waliduje przynależność benefitów i sam wybiera kolekcję na podstawie
      // typu kampanii. Klient nie wskazuje już nazwy kolekcji zamówień.
      const result = await this.apiService.placeOrder(type, orderObject);
      return !!result;
    } catch (e) {
      console.log(e);
      return false;
    }
  }

  private async getUserOrders(type: OrderType, currentUser: User): Promise<Array<any>> {
    try {
      const email = currentUser.email ? currentUser.email : '';
      if (type === 'airconditioners') {
        return await this.mongoService.getUserAirconditionersOrdersByEmail(email);
      }
      return await this.mongoService.getUserPumpsOrdersByEmail(email);
    } catch (e) {
      console.log(e);
      return [];
    }
  }

  private async generateConfirmationEmailTemplateData(
    type: OrderType,
    cartService: CartService,
    orderData: any,
    userDataService: UserDataService
  ): Promise<any> {
    const usrAdditionalData = await userDataService.getUserDataByEmail(
      String(userDataService.currentUser?.email)
    );

    const items: Array<any> = [];
    cartService?.cartItems?.forEach(i => {
      items.push({
        name: String(i.name).toString(),
        quantity: String(i.quantity).toString(),
        unitPrice: String(i.unitPrice).toString()
      });
    });

    return {
      userEmail: String(orderData.userEmail).toString(),
      firstName: String(usrAdditionalData.firstName).toString(),
      lastName: String(usrAdditionalData.lastName).toString(),
      streetName: String(usrAdditionalData.streetName).toString(),
      buildingNumber: String(usrAdditionalData.buildingNumber).toString(),
      postalCode: String(usrAdditionalData.postalCode).toString(),
      city: String(usrAdditionalData.city).toString(),
      voivodeship: String(usrAdditionalData.voivodeship).toString(),
      phoneNumber: String(usrAdditionalData.phoneNumber).toString(),
      totalPrice: String(cartService.getCartTotalPrice()).toString(),
      points: type == 'pumps' ? String(usrAdditionalData.pumpPrizeAmount).toString() : String(usrAdditionalData.airConditionPrizeAmount).toString(),
      items: items,
      campaignName: type == 'pumps' ? "POMP CIEPŁA PANASONIC" : "KLIMATYZACJA PANASONIC"
    };
  }

  private generateOrderObject(type: OrderType, cartService: CartService, currentUser: User): any {
    return {
      userEmail: currentUser?.email ?? '',
      userId: currentUser?.uid ?? '',
      campaignType: type,
      priceSum: cartService.getCartTotalPrice(),
      orderDate: new Date(),
      items: this.flattenCartContent(cartService),
      // Dane wybranego Paczkomatu zapisywane razem z zamówieniem (MongoDB).
      inpostParcelLocker: this.buildParcelLockerPayload(cartService),
    };
  }

  // Przekazujemy tylko publiczne pola punktu — backend i tak je rewaliduje/sanityzuje.
  private buildParcelLockerPayload(cartService: CartService): any {
    const parcelLocker = cartService.selectedParcelLocker;
    if (!parcelLocker) return null;
    return {
      name: parcelLocker.name,
      address: parcelLocker.address,
      city: parcelLocker.city,
      postCode: parcelLocker.postCode,
    };
  }

  private flattenCartContent(cartService: CartService): Array<any> {
    const content: Array<any> = [];
    cartService?.cartItems?.forEach(i => {
      content.push({
        benefitId: i.benefitId,
        unitPrice: i.unitPrice,
        quantity: i.quantity
      });
    });
    return content;
  }
}